import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { api } from '../services/api';

const PER_PAGE = parseInt(process.env.REACT_APP_PAGINATION_LIMIT || '10', 10);

/**
 * Encapsulates all state, data-fetching, filtering, pagination, and CRUD
 * event handlers for the Feedback Management list page.
 *
 * Keeping this logic out of the component makes FeedbackManagement.jsx a
 * pure presentation layer and makes the business logic independently testable.
 */
export function useFeedbackList() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, feedback: null });
  const [actionLoading, setActionLoading] = useState(null);

  const [detailModal, setDetailModal] = useState({ open: false, uuid: null });

  // Filters are applied on Search click, not on every keystroke.
  const [appliedFilters, setAppliedFilters] = useState({});

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { status: '', category: '', date_from: '', date_to: '' },
  });

  // ── Data fetching ────────────────────────────────────────────────────────

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: PER_PAGE, ...appliedFilters };
      const response = await api.admin.getFeedbacks(params);
      setFeedbacks(response.feedbacks || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Failed to fetch feedbacks:', error);
      toast.error('Failed to fetch feedback entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedFilters]);

  // ── Filter handlers ───────────────────────────────────────────────────────

  const onFilterSubmit = (data) => {
    const filters = {};
    if (data.status) filters.status = data.status;
    if (data.category) filters.category = data.category;
    if (data.date_from) filters.date_from = data.date_from;
    if (data.date_to) filters.date_to = data.date_to;
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleClearFilters = () => {
    reset();
    setAppliedFilters({});
    setPage(1);
  };

  // ── Pagination / refresh ──────────────────────────────────────────────────

  const handlePageChange = (_event, value) => setPage(value);

  const handleRefresh = () => fetchFeedbacks();

  // ── Delete handlers ───────────────────────────────────────────────────────

  const handleOpenDelete = (feedback) => setDeleteDialog({ open: true, feedback });

  const handleCloseDelete = () => setDeleteDialog({ open: false, feedback: null });

  const handleDelete = async () => {
    const { feedback } = deleteDialog;
    setActionLoading(feedback.uuid);
    try {
      await api.admin.deleteFeedback(feedback.uuid);
      toast.success('Feedback deleted successfully');
      handleCloseDelete();
      fetchFeedbacks();
    } catch (error) {
      console.error('Failed to delete feedback:', error);
      toast.error('Failed to delete feedback');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Detail modal handlers ─────────────────────────────────────────────────

  const handleOpenDetail = (uuid) => setDetailModal({ open: true, uuid });

  const handleCloseDetail = () => setDetailModal({ open: false, uuid: null });

  // ── Derived values ────────────────────────────────────────────────────────

  const hasActiveFilters = Object.keys(appliedFilters).length > 0;
  const totalPages = Math.ceil(total / PER_PAGE);

  return {
    // State
    feedbacks,
    loading,
    total,
    page,
    perPage: PER_PAGE,
    deleteDialog,
    actionLoading,
    detailModal,
    hasActiveFilters,
    totalPages,

    // Form (react-hook-form)
    control,
    handleSubmit,
    onFilterSubmit,
    handleClearFilters,

    // Event handlers
    fetchFeedbacks,
    handlePageChange,
    handleRefresh,
    handleOpenDelete,
    handleCloseDelete,
    handleDelete,
    handleOpenDetail,
    handleCloseDetail,
  };
}
